import { Link } from 'react-router-dom';
import { ArrowLeft, CircleDot, CircleCheck, CircleDashed } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

type Status = 'shipped' | 'in-progress' | 'planned';

interface Item {
  status: Status;
  title: string;
  body: string;
}

interface Quarter {
  label: string;
  meta: string;
  items: Item[];
}

const PLAN: Quarter[] = [
  {
    label: 'Q2 2026',
    meta: 'Сейчас в&nbsp;работе',
    items: [
      { status: 'shipped', title: 'Cookie-баннер и&nbsp;юр.&nbsp;база', body: 'Полный комплект документов под 152-ФЗ.' },
      { status: 'shipped', title: 'WebP, self-host Inter, code split', body: 'Лендинг загружается быстрее.' },
      { status: 'in-progress', title: 'Интеграция с&nbsp;ЮKassa Recurring', body: 'Автосписания подписки и&nbsp;карта вместо ручной оплаты.' },
      { status: 'in-progress', title: 'Импорт меню из&nbsp;iiko/r_keeper', body: 'CSV-импорт с&nbsp;модификаторами и&nbsp;стоп-листом.' },
      { status: 'planned', title: 'Telegram-бот заказа из&nbsp;меню', body: 'Гость заказывает, бот пингует кассу.' },
    ],
  },
  {
    label: 'Q3 2026',
    meta: 'Следующий квартал',
    items: [
      { status: 'planned', title: 'Складской учёт и&nbsp;себестоимость', body: 'Технические карты, остатки, расход по&nbsp;продажам.' },
      { status: 'planned', title: 'Программа лояльности', body: 'Накопительные баллы и&nbsp;персональные предложения.' },
      { status: 'planned', title: 'Доставка и&nbsp;агрегаторы', body: 'Объединение Я.Еда / Delivery&nbsp;Club / собственная доставка в&nbsp;одной кассе.' },
      { status: 'planned', title: '1С: Бухгалтерия', body: 'Выгрузка реализации, ТТН и&nbsp;сверки в&nbsp;один клик.' },
    ],
  },
  {
    label: 'Q4 2026',
    meta: 'Дальше по&nbsp;дорожной карте',
    items: [
      { status: 'planned', title: 'Оплата СБП по&nbsp;QR-меню', body: 'Гость сканирует чек, оплачивает мгновенно, без карты.' },
      { status: 'planned', title: 'Мобильный диспетчер сети', body: 'Один телефон управляет 10&nbsp;заведениями.' },
      { status: 'planned', title: 'Открытое API и&nbsp;Webhook', body: 'Для собственных интеграций и&nbsp;BI.' },
    ],
  },
];

const STATUS_META: Record<Status, { label: string; icon: typeof CircleDot; cls: string; dot: string }> = {
  shipped: { label: 'Готово', icon: CircleCheck, cls: 'text-ink/55 line-through', dot: 'text-orange' },
  'in-progress': { label: 'В работе', icon: CircleDot, cls: 'text-ink', dot: 'text-orange' },
  planned: { label: 'Запланировано', icon: CircleDashed, cls: 'text-ink/85', dot: 'text-ink/40' },
};

export default function Roadmap() {
  return (
    <div className="min-h-screen bg-paper">
      <Header />

      <main className="section">
        <div className="container-site">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-medium text-ink/60 transition hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            На главную
          </Link>

          <div className="mt-10 max-w-3xl">
            <span className="eyebrow">Roadmap</span>
            <h1 className="h2 mt-4">Куда мы&nbsp;идём</h1>
            <p className="mt-6 text-[15px] leading-relaxed text-ink/70">
              Открытая дорожная карта на&nbsp;ближайшие три квартала. Сроки могут сдвигаться,
              это нормально, но&nbsp;мы&nbsp;стараемся не&nbsp;обещать того, чего точно не&nbsp;сделаем.
              Если хотите быстрее увидеть конкретную фичу,{' '}
              <Link to="/support" className="link">
                напишите в&nbsp;поддержку
              </Link>
              .
            </p>
          </div>

          <div className="mt-12 grid gap-12 md:grid-cols-3">
            {PLAN.map((q) => (
              <div key={q.label}>
                <div className="border-b border-line pb-3">
                  <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                    <span dangerouslySetInnerHTML={{ __html: q.meta }} />
                  </div>
                  <div className="mt-1 text-[24px] font-medium tracking-tight text-ink">
                    {q.label}
                  </div>
                </div>
                <ul className="mt-5 space-y-5">
                  {q.items.map((it, i) => {
                    const meta = STATUS_META[it.status];
                    const Icon = meta.icon;
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <Icon
                          size={16}
                          strokeWidth={1.75}
                          className={'mt-0.5 shrink-0 ' + meta.dot}
                        />
                        <div>
                          <div
                            className={'text-[14px] font-medium leading-snug ' + meta.cls}
                            dangerouslySetInnerHTML={{ __html: it.title }}
                          />
                          <div
                            className="mt-1 text-[12.5px] leading-relaxed text-ink/60"
                            dangerouslySetInnerHTML={{ __html: it.body }}
                          />
                          <div className="mt-1 text-[10.5px] font-medium uppercase tracking-[0.14em] text-ink/40">
                            {meta.label}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-14 border border-line bg-white p-6 sm:p-8">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-xl">
                <div className="eyebrow">Не нашли своё</div>
                <h2 className="h3 mt-3">Скажите, что для вас критично</h2>
                <p className="mt-2 text-[14px] leading-relaxed text-ink/65">
                  Здесь не&nbsp;список «хотелок», а&nbsp;то, что мы&nbsp;реально делаем.
                  Если ваша задача не&nbsp;закрыта, пишите. Часто это значит, что
                  фичу можно поднять выше по&nbsp;приоритету.
                </p>
              </div>
              <Link to="/support" className="btn-primary">
                Написать в&nbsp;поддержку
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
